import {
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

import {
  CalendarDays,
  MapPin,
} from 'lucide-react';

import {
  useSelector,
} from 'react-redux';

import type {
  RootState,
} from '../../../app/store';

import Pagination from '../../../shared/components/Pagination';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import {
  attendanceService,
} from '../../attendance/services/attendanceService';

import type {
  AttendanceRankingItem,
  AttendanceRankingResponse,
} from '../../attendance/types/attendance.types';

import {
  storeService,
} from '../../store/services/storeService';

import type {
  StoreListItem,
} from '../../store/types/store.types';

type StoreManagementTab =
  | 'store_point_minting'
  | 'attendance';

type ApiErrorPayload = {
  message?: string;
};

const ATTENDANCE_PAGE_SIZE =
  10;

const getErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (
    axios.isAxiosError(error)
  ) {
    const payload =
      error.response?.data as
        | ApiErrorPayload
        | undefined;

    return (
      payload?.message ||
      error.message ||
      fallback
    );
  }

  if (
    error instanceof Error
  ) {
    return (
      error.message ||
      fallback
    );
  }

  return fallback;
};

export default function AdminSystemManagePage() {
  const modal =
    useModal();

  const authUser =
    useSelector(
      (
        state: RootState,
      ) =>
        state.auth.user,
    );

  const [
    currentTab,
    setCurrentTab,
  ] =
    useState<StoreManagementTab>(
      'store_point_minting',
    );

  const [
    stores,
    setStores,
  ] =
    useState<StoreListItem[]>(
      [],
    );

  const [
    loadingStores,
    setLoadingStores,
  ] = useState(false);

  const [
    storePointMintAmount,
    setStorePointMintAmount,
  ] = useState('');

  const [
    loadingStorePoint,
    setLoadingStorePoint,
  ] = useState(false);

  const now =
    new Date();

  const [
    attendanceYear,
    setAttendanceYear,
  ] = useState(
    now.getFullYear(),
  );

  const [
    attendanceMonth,
    setAttendanceMonth,
  ] = useState(
    now.getMonth() + 1,
  );

  const [
    attendanceData,
    setAttendanceData,
  ] =
    useState<AttendanceRankingResponse | null>(
      null,
    );

  const [
    attendancePage,
    setAttendancePage,
  ] = useState(1);

  const [
    attendanceLoading,
    setAttendanceLoading,
  ] = useState(false);

  const storeId =
    authUser?.storeId ?? 0;

  const currentStore =
    stores.find(
      (store) =>
        store.storeId ===
        storeId,
    ) ?? null;

  /**
   * 매장 목록 조회
   */
  useEffect(() => {
    let isActive =
      true;

    const loadStores =
      async (): Promise<void> => {
        try {
          setLoadingStores(
            true,
          );

          const data =
            await storeService.getStores();

          if (!isActive) {
            return;
          }

          setStores(data);
        } catch (
          error: unknown
        ) {
          if (!isActive) {
            return;
          }

          const message =
            getErrorMessage(
              error,
              '지점 목록 조회 실패',
            );

          await modal.alert(
            message,
          );
        } finally {
          if (isActive) {
            setLoadingStores(
              false,
            );
          }
        }
      };

    void loadStores();

    return () => {
      isActive = false;
    };
  }, [modal]);

  /**
   * 매장 포인트 발행
   */
  const handleStorePointMint =
    async (): Promise<void> => {
      if (!storeId) {
        await modal.alert(
          '소속 지점 정보가 없습니다.',
        );

        return;
      }

      const amount =
        Number(
          storePointMintAmount,
        );

      if (
        !Number.isFinite(
          amount,
        ) ||
        amount <= 0
      ) {
        await modal.alert(
          '올바른 매장 포인트를 입력해주세요.',
        );

        return;
      }

      const confirmed =
        await modal.confirm(
          `${
            currentStore
              ?.storeName ??
            '내 지점'
          }에 ${amount.toLocaleString(
            'ko-KR',
          )} 매장 포인트를 발행하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoadingStorePoint(
          true,
        );

        await storeService
          .mintStorePoints(
            storeId,
            amount,
          );

        await modal.alert(
          '매장 포인트가 발행되었습니다.',
        );

        setStorePointMintAmount(
          '',
        );
      } catch (
        error: unknown
      ) {
        const message =
          getErrorMessage(
            error,
            '매장 포인트 발행 실패',
          );

        await modal.alert(
          message,
        );
      } finally {
        setLoadingStorePoint(
          false,
        );
      }
    };

  /**
   * 출석 랭킹 조회
   */
  const handleFetchAttendance =
    async (
      page: number = 1,
    ): Promise<void> => {
      if (!storeId) {
        await modal.alert(
          '소속 지점 정보가 없습니다.',
        );

        return;
      }

      try {
        setAttendanceLoading(
          true,
        );

        const data =
          await attendanceService
            .getAttendanceRanking(
              {
                storeId,

                year:
                  attendanceYear,

                month:
                  attendanceMonth,

                /*
                 * API는 0부터 시작
                 * UI는 1부터 시작
                 */
                page:
                  page - 1,

                size:
                  ATTENDANCE_PAGE_SIZE,
              },
            );

        setAttendanceData(
          data,
        );

        setAttendancePage(
          page,
        );
      } catch (
        error: unknown
      ) {
        const message =
          getErrorMessage(
            error,
            '출석 조회 실패',
          );

        await modal.alert(
          message,
        );
      } finally {
        setAttendanceLoading(
          false,
        );
      }
    };

  const daysInMonth =
    new Date(
      attendanceYear,
      attendanceMonth,
      0,
    ).getDate();

  return (
    <main className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          매장 관리
        </h1>

        <p className="mt-2 text-sm text-gray-600">
          소속 지점의 매장 포인트와
          출석 현황을 관리합니다.
        </p>
      </header>

      {/* Tab */}
      <nav className="mb-6 flex gap-2 overflow-x-auto border-b border-gray-200">
        <button
          type="button"
          onClick={() =>
            setCurrentTab(
              'store_point_minting',
            )
          }
          className={`shrink-0 border-b-2 px-4 py-3 font-medium transition ${
            currentTab ===
            'store_point_minting'
              ? 'border-main text-main'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="flex items-center gap-2">
            <MapPin
              size={18}
            />

            매장 포인트 발행
          </span>
        </button>

        <button
          type="button"
          onClick={() =>
            setCurrentTab(
              'attendance',
            )
          }
          className={`shrink-0 border-b-2 px-4 py-3 font-medium transition ${
            currentTab ===
            'attendance'
              ? 'border-main text-main'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="flex items-center gap-2">
            <CalendarDays
              size={18}
            />

            출석 조회
          </span>
        </button>
      </nav>

      <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
        {/* ======================
            매장 포인트 발행
        ====================== */}
        {currentTab ===
          'store_point_minting' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              매장 포인트 발행
            </h2>

            <p className="mt-2 text-sm text-gray-600">
              내 지점에 사용할
              매장 포인트를 발행합니다.
            </p>

            <div className="mt-6 max-w-md">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                소속 지점
              </label>

              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-100 px-4 py-3 text-gray-700">
                {loadingStores
                  ? '지점 정보를 불러오는 중...'
                  : currentStore
                      ?.storeName ??
                    '등록된 지점 없음'}
              </div>

              <label className="mb-2 block text-sm font-medium text-gray-700">
                발행할 매장 포인트
              </label>

              <input
                type="number"
                min="1"
                value={
                  storePointMintAmount
                }
                onChange={(
                  event,
                ) =>
                  setStorePointMintAmount(
                    event.target
                      .value,
                  )
                }
                placeholder="매장 포인트 입력"
                disabled={
                  loadingStorePoint
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-3 outline-none focus:border-main disabled:cursor-not-allowed disabled:bg-gray-100"
              />

              <button
                type="button"
                onClick={() =>
                  void handleStorePointMint()
                }
                disabled={
                  loadingStorePoint ||
                  !storeId ||
                  !storePointMintAmount
                }
                className="mt-4 w-full rounded-lg bg-main px-6 py-3 font-medium text-white transition hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingStorePoint
                  ? '발행 중...'
                  : '발행하기'}
              </button>
            </div>
          </div>
        )}

        {/* ======================
            출석 랭킹
        ====================== */}
        {currentTab ===
          'attendance' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              출석 랭킹 조회
            </h2>

            <p className="mt-2 text-sm text-gray-600">
              내 지점의 월별 회원
              출석 현황을 확인합니다.
            </p>

            <div className="mt-6 flex flex-wrap items-end gap-4">
              {/* Store */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  지점
                </label>

                <div className="rounded-lg border border-gray-200 bg-gray-100 px-4 py-2 text-gray-700">
                  {loadingStores
                    ? '조회 중...'
                    : currentStore
                        ?.storeName ??
                      '등록된 지점 없음'}
                </div>
              </div>

              {/* Year */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  연도
                </label>

                <select
                  value={
                    attendanceYear
                  }
                  onChange={(
                    event,
                  ) => {
                    setAttendanceYear(
                      Number(
                        event.target
                          .value,
                      ),
                    );

                    setAttendanceData(
                      null,
                    );

                    setAttendancePage(
                      1,
                    );
                  }}
                  disabled={
                    attendanceLoading
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 outline-none focus:border-main disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  {[
                    now.getFullYear() -
                      1,

                    now.getFullYear(),

                    now.getFullYear() +
                      1,
                  ].map(
                    (year) => (
                      <option
                        key={year}
                        value={year}
                      >
                        {year}년
                      </option>
                    ),
                  )}
                </select>
              </div>

              {/* Month */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  월
                </label>

                <select
                  value={
                    attendanceMonth
                  }
                  onChange={(
                    event,
                  ) => {
                    setAttendanceMonth(
                      Number(
                        event.target
                          .value,
                      ),
                    );

                    setAttendanceData(
                      null,
                    );

                    setAttendancePage(
                      1,
                    );
                  }}
                  disabled={
                    attendanceLoading
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 outline-none focus:border-main disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  {Array.from(
                    {
                      length: 12,
                    },
                    (
                      _,
                      index,
                    ) =>
                      index + 1,
                  ).map(
                    (month) => (
                      <option
                        key={
                          month
                        }
                        value={
                          month
                        }
                      >
                        {month}월
                      </option>
                    ),
                  )}
                </select>
              </div>

              <button
                type="button"
                onClick={() =>
                  void handleFetchAttendance(
                    1,
                  )
                }
                disabled={
                  attendanceLoading ||
                  !storeId
                }
                className="rounded-lg bg-main px-6 py-2 font-medium text-white transition hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                {attendanceLoading
                  ? '조회 중...'
                  : '조회'}
              </button>
            </div>

            {/* Result */}
            {attendanceData && (
              <div className="mt-6">
                {attendanceData
                  .rankings
                  .length ===
                0 ? (
                  <div className="py-12 text-center text-gray-500">
                    출석 내역이 없습니다.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="whitespace-nowrap border-b border-r border-gray-200 px-3 py-3 text-left">
                            닉네임
                          </th>

                          <th className="whitespace-nowrap border-b border-r border-gray-200 px-3 py-3 text-center">
                            출석수
                          </th>

                          {Array.from(
                            {
                              length:
                                daysInMonth,
                            },
                            (
                              _,
                              index,
                            ) =>
                              index + 1,
                          ).map(
                            (day) => (
                              <th
                                key={
                                  day
                                }
                                className="w-8 whitespace-nowrap border-b border-r border-gray-200 px-2 py-3 text-center"
                              >
                                {
                                  day
                                }
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {attendanceData.rankings.map(
                          (
                            item: AttendanceRankingItem,
                          ) => (
                            <tr
                              key={
                                item.userId
                              }
                              className="hover:bg-gray-50"
                            >
                              <td className="whitespace-nowrap border-b border-r border-gray-200 px-3 py-3">
                                {
                                  item.nickname
                                }
                              </td>

                              <td className="border-b border-r border-gray-200 px-3 py-3 text-center">
                                {
                                  item.attendanceCount
                                }
                              </td>

                              {Array.from(
                                {
                                  length:
                                    daysInMonth,
                                },
                                (
                                  _,
                                  index,
                                ) =>
                                  `${attendanceMonth}/${index + 1}`,
                              ).map(
                                (
                                  dateKey,
                                ) => (
                                  <td
                                    key={
                                      dateKey
                                    }
                                    className="w-8 border-b border-r border-gray-200 px-2 py-3 text-center"
                                  >
                                    {item
                                      .attendanceByDate[
                                      dateKey
                                    ] ? (
                                      <span className="font-bold text-main">
                                        O
                                      </span>
                                    ) : null}
                                  </td>
                                ),
                              )}
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={
                    attendancePage
                  }
                  totalItems={
                    attendanceData
                      .totalCount
                  }
                  itemsPerPage={
                    ATTENDANCE_PAGE_SIZE
                  }
                  onPageChange={(
                    page,
                  ) =>
                    void handleFetchAttendance(
                      page,
                    )
                  }
                />
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
}